import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Plus, Edit, Trash2, Eye, Grid, List,
  MoreVertical, Package, Clock, CheckCircle, XCircle, AlertCircle,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import { toast } from 'react-toastify';
import Layout from '../../components/Layout';
import {
  useGetCategoriesQuery,
  useToggleCategoryStatusMutation,
  useDeleteCategoryMutation,
} from '../../app/api';

const Categories = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [filterStatus, setFilterStatus] = useState('All');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  // ✅ RTK Query
  const { data, isLoading } = useGetCategoriesQuery({
    page,
    limit,
    search: searchTerm || undefined,
    status: filterStatus !== 'All' ? filterStatus : undefined,
  });

  const [toggleStatus] = useToggleCategoryStatusMutation();
  const [deleteCategory] = useDeleteCategoryMutation();

  const categories = data?.data?.categories || [];
  const pagination = data?.data?.pagination || {};
  const imageBaseUrl = data?.data?.imageBaseUrl || '';

  const statusOptions = ['All', 'Active', 'Inactive'];

  const getStatusBadge = (status) => ({
    Active: 'badge-success',
    Inactive: 'badge-danger',
  }[status] || 'badge-info');

  const getStatusIcon = (status) => ({
    Active: <CheckCircle size={14} />,
    Inactive: <XCircle size={14} />,
  }[status] || <AlertCircle size={14} />);

  const getFullImage = (imagePath) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http')) return imagePath;
    return `${imageBaseUrl}${imagePath}`;
  };

  const handleToggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'Active' ? 'Inactive' : 'Active';
    if (!window.confirm(`Change status to ${newStatus}?`)) return;

    setTogglingId(id);
    try {
      await toggleStatus(id).unwrap();
      toast.success(`Status: ${newStatus}`);
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to toggle status');
    } finally {
      setTogglingId(null);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      await deleteCategory(id).unwrap();
      toast.success('Category deleted');
      if (categories.length === 1 && page > 1) setPage(page - 1);
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to delete category');
    }
  };

  const handleViewCategory = (category) => {
    setSelectedCategory(category);
    setShowCategoryModal(true);
  };

  const handleEditCategory = (category) => navigate(`/categories/edit/${category._id}`);
  const handleAddCategory = () => navigate('/categories/add');
  const closeViewModal = () => {
    setShowCategoryModal(false);
    setSelectedCategory(null);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    setPage(newPage);
  };

  const handleLimitChange = (e) => {
    setLimit(parseInt(e.target.value));
    setPage(1);
  };

  const getPageNumbers = () => {
    const { page: curPage, totalPages } = pagination;
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, curPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);

    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push('...');
    }
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages) {
      if (end < totalPages - 1) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <Layout>
      <div className="categories-page">
        {/* Header */}
        <div className="page-header">
          <div>
            <h1 className="page-title">Categories</h1>
            <p className="page-subtitle">Manage your product categories</p>
          </div>
          <div className="page-actions">
            <button className={`btn btn-sm ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setViewMode('grid')}>
              <Grid size={16} />
            </button>
            <button className={`btn btn-sm ${viewMode === 'list' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setViewMode('list')}>
              <List size={16} />
            </button>
            <button className="btn btn-primary" onClick={handleAddCategory}>
              <Plus size={18} /> Add Category
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="categories-stats">
          <div className="stat-item">
            <div className="stat-icon" style={{ background: 'var(--color-primary)' }}><Grid size={20} /></div>
            <div><span className="stat-label">Total</span><span className="stat-value">{pagination.total || 0}</span></div>
          </div>
          <div className="stat-item">
            <div className="stat-icon" style={{ background: 'var(--color-success)' }}><CheckCircle size={20} /></div>
            <div>
              <span className="stat-label">Active (Page)</span>
              <span className="stat-value">{categories.filter((c) => c.status === 'Active').length}</span>
            </div>
          </div>
          <div className="stat-item">
            <div className="stat-icon" style={{ background: 'var(--color-danger)' }}><XCircle size={20} /></div>
            <div>
              <span className="stat-label">Inactive (Page)</span>
              <span className="stat-value">{categories.filter((c) => c.status === 'Inactive').length}</span>
            </div>
          </div>
          <div className="stat-item">
            <div className="stat-icon" style={{ background: 'var(--color-secondary)' }}><Package size={20} /></div>
            <div>
              <span className="stat-label">Products</span>
              <span className="stat-value">{categories.reduce((s, c) => s + (c.products || 0), 0)}</span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="categories-filters card">
          <div className="filter-row">
            <div className="filter-group">
              <div className="search-box">
                <Search size={18} className="search-icon" />
                <input
                  type="text"
                  placeholder="Search categories..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                  className="search-input"
                />
                {searchTerm && <button className="clear-search" onClick={() => setSearchTerm('')}>✕</button>}
              </div>
            </div>
            <div className="filter-group">
              <select
                className="filter-select"
                value={filterStatus}
                onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
              >
                {statusOptions.map((s) => (
                  <option key={s} value={s}>{s === 'All' ? 'All Status' : s}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <select className="filter-select" value={limit} onChange={handleLimitChange}>
                <option value="10">10 / page</option>
                <option value="25">25 / page</option>
                <option value="50">50 / page</option>
                <option value="100">100 / page</option>
              </select>
            </div>
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex-center" style={{ height: '300px' }}><div className="spinner" /></div>
        ) : viewMode === 'grid' ? (
          <div className="categories-grid">
            {categories.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-content">
                  <span className="empty-icon">📂</span>
                  <h3>No categories found</h3>
                </div>
              </div>
            ) : categories.map((category) => (
              <div key={category._id} className="category-card">
                <div className="category-card-header">
                  <div className="category-icon-large">
                    {category.image ? <img src={getFullImage(category.image)} alt={category.name} className="category-thumb" /> : '📦'}
                  </div>
                  <div className="category-card-info">
                    <h4 className="category-name">{category.name}</h4>
                    <span className="category-slug">/{category.slug}</span>
                  </div>
                  <button className="btn btn-icon" onClick={() => handleViewCategory(category)}>
                    <MoreVertical size={18} />
                  </button>
                </div>
                <div className="category-card-body">
                  <p className="category-description">{category.description || 'No description'}</p>
                  <div className="category-meta">
                    <div className="meta-item"><Package size={14} /><span>{category.products || 0} Products</span></div>
                    <div className="meta-item">
                      <Clock size={14} />
                      <span>Created {new Date(category.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                <div className="category-card-footer">
                  <button
                    className={`badge ${getStatusBadge(category.status)} status-toggle-btn`}
                    onClick={() => handleToggleStatus(category._id, category.status)}
                    disabled={togglingId === category._id}
                  >
                    {togglingId === category._id ? <span className="spinner-sm" /> : getStatusIcon(category.status)}
                    {category.status}
                  </button>
                  <div className="category-card-actions">
                    <button className="btn btn-sm btn-outline" onClick={() => handleViewCategory(category)}><Eye size={14} /></button>
                    <button className="btn btn-sm btn-primary" onClick={() => handleEditCategory(category)}><Edit size={14} /></button>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDeleteCategory(category._id)}><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="card categories-table-container">
            <div className="table-container">
              <table className="table categories-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Slug</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th style={{ width: '120px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.length === 0 ? (
                    <tr><td colSpan="5" className="empty-state"><h3>No categories found</h3></td></tr>
                  ) : categories.map((category) => (
                    <tr key={category._id} className="category-row">
                      <td>
                        <div className="category-info">
                          <div className="category-icon-small">
                            {category.image ? <img src={getFullImage(category.image)} alt={category.name} className="category-thumb" /> : '📦'}
                          </div>
                          <span className="category-name">{category.name}</span>
                        </div>
                      </td>
                      <td>/{category.slug}</td>
                      <td>
                        <button
                          className={`badge ${getStatusBadge(category.status)} status-toggle-btn`}
                          onClick={() => handleToggleStatus(category._id, category.status)}
                          disabled={togglingId === category._id}
                        >
                          {togglingId === category._id ? <span className="spinner-sm" /> : getStatusIcon(category.status)}
                          {category.status}
                        </button>
                      </td>
                      <td>
                        {new Date(category.createdAt).toLocaleDateString('en-IN', {
                          day: '2-digit', month: 'short', year: 'numeric',
                        })}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button className="btn btn-sm btn-outline" onClick={() => handleViewCategory(category)}><Eye size={16} /></button>
                          <button className="btn btn-sm btn-primary" onClick={() => handleEditCategory(category)}><Edit size={16} /></button>
                          <button className="btn btn-sm btn-danger" onClick={() => handleDeleteCategory(category._id)}><Trash2 size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.total > 0 && (
              <div className="table-footer">
                <div className="table-info">
                  Showing <strong>{(pagination.page - 1) * pagination.limit + 1}</strong> to{' '}
                  <strong>{Math.min(pagination.page * pagination.limit, pagination.total)}</strong> of{' '}
                  <strong>{pagination.total}</strong> categories
                </div>
                <div className="pagination">
                  <button className="btn btn-sm btn-outline" onClick={() => handlePageChange(pagination.page - 1)} disabled={!pagination.hasPrevPage}>
                    <ChevronLeft size={16} /> Prev
                  </button>
                  {getPageNumbers().map((p, i) => p === '...' ? (
                    <span key={`dots-${i}`} className="pagination-dots">...</span>
                  ) : (
                    <button key={p} className={`btn btn-sm ${pagination.page === p ? 'btn-primary' : 'btn-outline'}`} onClick={() => handlePageChange(p)}>{p}</button>
                  ))}
                  <button className="btn btn-sm btn-outline" onClick={() => handlePageChange(pagination.page + 1)} disabled={!pagination.hasNextPage}>
                    Next <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* View Modal */}
      {showCategoryModal && selectedCategory && (
        <div className="modal-overlay" onClick={closeViewModal}>
          <div className="modal category-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-category-info">
                <div className="modal-category-icon">
                  {selectedCategory.image ? <img src={getFullImage(selectedCategory.image)} alt={selectedCategory.name} className="category-thumb-lg" /> : '📦'}
                </div>
                <div>
                  <h2 className="modal-title">{selectedCategory.name}</h2>
                  <p className="modal-subtitle">/{selectedCategory.slug}</p>
                </div>
              </div>
              <button className="modal-close" onClick={closeViewModal}>✕</button>
            </div>
            <div className="category-modal-content">
              <div className="category-detail-row">
                <span className="detail-label">Description</span>
                <span className="detail-value">{selectedCategory.description || 'No description'}</span>
              </div>
              <div className="category-detail-row">
                <span className="detail-label">Status</span>
                <span className="detail-value">
                  <span className={`badge ${getStatusBadge(selectedCategory.status)}`}>{selectedCategory.status}</span>
                </span>
              </div>
              <div className="category-detail-row">
                <span className="detail-label">Products</span>
                <span className="detail-value">{selectedCategory.products || 0} products</span>
              </div>
              <div className="category-detail-row">
                <span className="detail-label">Created</span>
                <span className="detail-value">
                  {new Date(selectedCategory.createdAt).toLocaleDateString('en-IN', {
                    day: '2-digit', month: 'short', year: 'numeric',
                  })}
                </span>
              </div>
              <div className="modal-actions">
                <button className="btn btn-outline" onClick={closeViewModal}>Close</button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    closeViewModal();
                    handleEditCategory(selectedCategory);
                  }}
                >
                  <Edit size={16} /> Edit Category
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Categories;